<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';
import { useGlobalStore } from '@/stores/global';
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useMediaQuery, useSwipe } from '@vueuse/core';
import PromoSlideCard from '@/components/cards/PromoSlideCard.vue';

const globalStore = useGlobalStore();
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  isCatalog: {
    type: Boolean,
    default: false,
  },
  isFirstSection: {
    type: Boolean,
    default: false,
  },
});

const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  items,
  button,
  hasTitle,
  hasItems,
  hasButton,
  template,
  hasAnyContent,
} = useBlockSafe(props.data);

const autoplay = false;
const autoplayDelay = 3000;
const loop = true;
const swipeThreshold = 50;
const isMobile = useMediaQuery('(max-width: 767px)');
const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
const isLargeDesktop = useMediaQuery('(min-width: 1440px)');
const isMounted = ref(false);
const breakpoint = computed(() => {
  if (!isMounted.value) return 'desktop';
  if (isMobile.value) return 'mobile';
  if (isTablet.value) return 'tablet';
  if (isLargeDesktop.value) return 'large';
  return 'desktop';
});

const hasAnyTitles = computed(() => {
  return items.value.some((slide) => slide.title && slide.title.length > 0);
});
const hasAnyDescription = computed(() => {
  return items.value.some(
    (slide) => slide.description && slide.description.length > 0,
  );
});

const isOnlyMedia = computed(() => {
  return !hasAnyTitles.value && !hasAnyDescription.value;
});

const activeIndex = ref(0);
const sliderContainer = ref(null);
const sliderWrapper = ref(null);
const autoplayInterval = ref(null);
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0);
useSwipe(sliderContainer, {
  threshold: swipeThreshold,
  onSwipeEnd(direction) {
    if (direction === 'left') nextSlide();
    else if (direction === 'right') prevSlide();
  },
});
const handleResize = () => {
  windowWidth.value = window.innerWidth;
  initializeSliderPosition();
  nextTick(() => {
    scrollToActiveSlide(false); // Без анімації при ресайзі
  });
};
// Mirrors assets/styles/utils/measures.scss's px-to-vw(): scales linearly
// between the reference and max viewport widths, then holds flat beyond
// that, so this JS-driven slider matches the CSS-driven scaling elsewhere.
const FLUID_REFERENCE_WIDTH = 1440;
const FLUID_MAX_WIDTH = 2560;
const FLUID_RATIO = FLUID_MAX_WIDTH / FLUID_REFERENCE_WIDTH;

const getFluidValue = (px, currentWidth) => {
  const maxPx = px * FLUID_RATIO;
  if (currentWidth <= FLUID_REFERENCE_WIDTH) return px;
  if (currentWidth >= FLUID_MAX_WIDTH) return maxPx;
  const slope = (maxPx - px) / (FLUID_MAX_WIDTH - FLUID_REFERENCE_WIDTH);
  return px + slope * (currentWidth - FLUID_REFERENCE_WIDTH);
};

const getAdaptiveValue = (mobile, tablet, desktop, largeBaseValue) => {
  if (breakpoint.value === 'mobile') return mobile;
  if (breakpoint.value === 'tablet') return tablet;
  if (breakpoint.value === 'large') {
    return getFluidValue(largeBaseValue, windowWidth.value);
  }
  return desktop;
};
const gap = computed(() => {
  return getAdaptiveValue(16, 16, 16, 16);
});

const slideHeight = computed(() => {
  return getAdaptiveValue(200, 368, 368, 368);
});

const activeSlideWidth = computed(() => {
  return getAdaptiveValue(200, 368, 368, 368);
});

const inactiveSlideWidth = computed(() => {
  return getAdaptiveValue(95, 184, 184, 184);
});
function getSlideImage(slide) {
  const main = slide?.image?.src ? slide.image : null;

  return {
    src: main?.src || slide?.src || '',
    alt: main?.alt || slide?.alt || '',
    width: main?.width || slide?.width || null,
    height: main?.height || slide?.height || null,
    mobileSrc: main?.src_mobile || slide?.src_mobile || '',
    tabletSrc: main?.src_tablet || slide?.src_tablet || '',
  };
}
const displaySlides = computed(() => {
  const list = items.value;

  const buildSlide = (slide, extra = {}) => ({
    ...slide,
    ...extra,
    resolvedImage: getSlideImage(slide),
  });

  if (!isMounted.value || list.length <= 1 || !sliderContainer.value) {
    return list.map((slide, index) =>
      buildSlide(slide, { originalIndex: index }),
    );
  }

  const containerWidth = sliderContainer.value.clientWidth;
  const currentGap = gap.value;

  let totalWidth = 0;
  list.forEach((_, index) => {
    const width =
      index === 0 ? activeSlideWidth.value : inactiveSlideWidth.value;
    totalWidth += width + currentGap;
  });
  totalWidth -= currentGap;

  if (totalWidth >= containerWidth) {
    return list.map((slide, index) =>
      buildSlide(slide, { originalIndex: index }),
    );
  }

  const result = [];
  let currentWidth = 0;
  let slideIndex = 0;
  let cloneRound = 0;

  while (currentWidth < containerWidth * 1.5) {
    const originalIndex = slideIndex % list.length;

    result.push(
      buildSlide(list[originalIndex], {
        originalIndex,
        cloneId: `${originalIndex}-${cloneRound}`,
      }),
    );

    const isActive = result.length - 1 === activeIndex.value;
    const width = isActive ? activeSlideWidth.value : inactiveSlideWidth.value;
    currentWidth += width + currentGap;

    slideIndex++;
    if (slideIndex % list.length === 0) {
      cloneRound++;
    }
  }

  return result;
});
const getRealSlideIndex = (index) => {
  if (!displaySlides.value[index]) return 0;
  return displaySlides.value[index].originalIndex;
};

const getSlideStyle = (index) => {
  const isActive = index === activeIndex.value;
  const width = isActive ? activeSlideWidth.value : inactiveSlideWidth.value;

  return {
    width: `${width}px`,
    height: `${slideHeight.value}px`,
    flex: `0 0 ${width}px`,
  };
};

const calculateSlidePosition = (slideIndex) => {
  const wrapper = sliderWrapper.value;
  if (!wrapper) return 0;

  const currentGap = gap.value;
  let position = 0;

  for (let i = 0; i < slideIndex; i++) {
    const width =
      i === activeIndex.value
        ? activeSlideWidth.value
        : inactiveSlideWidth.value;
    position += width + currentGap;
  }

  return position;
};

const scrollToActiveSlide = (smooth = true) => {
  const container = sliderContainer.value;
  if (!container) return;

  const containerWidth = container.clientWidth;
  const activeSlidePos = calculateSlidePosition(activeIndex.value);
  const currentActiveWidth = activeSlideWidth.value;

  const scrollPosition =
    activeSlidePos - containerWidth / 2 + currentActiveWidth / 2;

  container.scrollTo({
    left: Math.max(0, scrollPosition),
    behavior: smooth ? 'smooth' : 'auto',
  });
};

const setActiveSlide = (index) => {
  activeIndex.value = index;
  nextTick(() => {
    scrollToActiveSlide();
  });
};

const setActiveSlideByOriginal = (originalIndex) => {
  const targetIndex = displaySlides.value.findIndex(
    (slide) => slide.originalIndex === originalIndex,
  );

  if (targetIndex !== -1) {
    setActiveSlide(targetIndex);
  }
};

const nextSlide = () => {
  if (activeIndex.value < displaySlides.value.length - 1) {
    setActiveSlide(activeIndex.value + 1);
  } else if (loop) {
    setActiveSlide(0);
  }
};

const prevSlide = () => {
  if (activeIndex.value > 0) {
    setActiveSlide(activeIndex.value - 1);
  } else if (loop) {
    setActiveSlide(displaySlides.value.length - 1);
  }
};

const startAutoplay = () => {
  if (autoplay) {
    autoplayInterval.value = setInterval(() => {
      nextSlide();
    }, autoplayDelay);
  }
};

const stopAutoplay = () => {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value);
    autoplayInterval.value = null;
  }
};

const handleMouseEnter = () => {
  if (autoplay) {
    stopAutoplay();
  }
};

const handleMouseLeave = () => {
  if (autoplay) {
    startAutoplay();
  }
};

const initializeSliderPosition = () => {
  nextTick(() => {
    setTimeout(() => {
      scrollToActiveSlide();
    }, 50);
  });
};

watch(breakpoint, () => {
  initializeSliderPosition();
});

watch(activeIndex, () => {
  nextTick(() => {
    scrollToActiveSlide();
  });
});

watch(
  displaySlides,
  () => {
    initializeSliderPosition();
  },
  { flush: 'post' },
);

onMounted(() => {
  isMounted.value = true;
  window.addEventListener('resize', handleResize);
  setTimeout(() => {
    initializeSliderPosition();
    startAutoplay();
  }, 100);

  if (sliderContainer.value) {
    sliderContainer.value.addEventListener('mouseenter', handleMouseEnter);
    sliderContainer.value.addEventListener('mouseleave', handleMouseLeave);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  stopAutoplay();

  if (sliderContainer.value) {
    sliderContainer.value.removeEventListener('mouseenter', handleMouseEnter);
    sliderContainer.value.removeEventListener('mouseleave', handleMouseLeave);
  }
});
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="images-slider">
        <div
          v-if="hasTitle || hasButton"
          class="images-slider__top"
          :class="[hasButton ? `images-slider__top--with-button` : '']"
          data-aos="fade-in"
          data-aos-delay="300"
        >
          <template v-if="hasTitle">
            <h1
              v-if="template === 'hero'"
              :class="[
                'mb-0 images-slider__title h1',
                backgroundColor === 'black' ? 'white' : 'black',
              ]"
            >
              <template v-if="isCatalog && globalStore.catalogPageTitle">
                {{ globalStore.catalogPageTitle }}
              </template>
              <template v-else>
                {{ title }}
              </template>
            </h1>
            <h2
              v-else
              :class="[
                'mb-0 images-slider__title h2',
                backgroundColor === 'black' ? 'white' : 'black',
              ]"
              v-html="title"
            ></h2>
          </template>
          <CTagDetect
            v-if="hasButton"
            :to="button?.url"
            :class="[
              'images-slider__button p-button-text button-icon-next',
              backgroundColor === 'black' ? 'white' : 'black',
            ]"
            >{{ button?.title }}
            <CIcon
              class="button-icon-next-icon"
              name="arrow-red"
              width="16"
              height="16"
            />
          </CTagDetect>
        </div>
        <div
          v-if="hasItems"
          class="images-slider__bottom"
          :class="{ 'images-slider__bottom--with-content': !isOnlyMedia }"
        >
          <div ref="sliderContainer" class="images-slider__media">
            <div ref="sliderWrapper" class="images-slider__items">
              <div
                v-for="(slide, index) in displaySlides"
                :key="`${slide.originalIndex}-${index}`"
                class="images-slider__item"
                :class="{
                  'images-slider__item--active': index === activeIndex,
                }"
                :style="getSlideStyle(index)"
                data-aos="fade-left"
                data-aos-delay="500"
                @click="setActiveSlide(index)"
              >
                <picture
                  class="images-slider__item-image"
                >
                  <source
                    v-if="slide.resolvedImage.mobileSrc"
                    media="(max-width: 767px)"
                    :srcset="slide.resolvedImage.mobileSrc"
                  />
                  <source
                    v-if="slide.resolvedImage.tabletSrc"
                    media="(min-width: 768px) and (max-width: 1023px)"
                    :srcset="slide.resolvedImage.tabletSrc"
                  />
                  <NuxtImg
                    :src="slide.resolvedImage.src"
                    :alt="slide.resolvedImage.alt"
                    :width="slide.resolvedImage.width"
                    :height="slide.resolvedImage.height"
                    :loading="isFirstSection && index === 0 ? 'eager' : 'lazy'"
                    :fetchpriority="
                      isFirstSection && index === 0 ? 'high' : undefined
                    "
                    draggable="false"
                  />
                </picture>
              </div>
            </div>
          </div>
          <Transition name="slide-content" mode="out-in">
            <PromoSlideCard
              v-if="!isOnlyMedia"
              class="images-slider__content"
              :title="items[getRealSlideIndex(activeIndex)].title"
              :description="items[getRealSlideIndex(activeIndex)].description"
              :duration="items[getRealSlideIndex(activeIndex)].duration"
              :url="items[getRealSlideIndex(activeIndex)].url"
              :view="template"
              :background-color="backgroundColor"
              data-aos="fade-left"
              data-aos-delay="500"
            />
          </Transition>
          <div
            v-if="items.length > 1"
            class="images-slider__navigation"
            data-aos="fade-in"
            data-aos-delay="500"
          >
            <Button
              type="button"
              rounded
              variant="outlined"
              class="images-slider__navigation-prev button-prev js-button-images-slider-prev"
              :aria-label="$t('slider.prev')"
              @click="prevSlide"
            >
              <CIcon
                class="button-prev__icon"
                :name="
                  backgroundColor === 'black'
                    ? 'direction-white'
                    : 'direction-black'
                "
                width="16"
                height="16"
              />
            </Button>
            <Button
              v-if="items.length > 1"
              type="button"
              rounded
              variant="outlined"
              class="images-slider__navigation-next button-next js-button-images-slider-next"
              :aria-label="$t('slider.next')"
              @click="nextSlide"
            >
              <CIcon
                class="button-next__icon"
                :name="
                  backgroundColor === 'black'
                    ? 'direction-white'
                    : 'direction-black'
                "
                width="16"
                height="16"
              />
            </Button>
          </div>
          <div
            v-if="items.length > 1"
            class="images-slider__pagination"
            data-aos="fade-in"
            data-aos-delay="500"
          >
            <button
              v-for="(slide, index) in items"
              :key="`dot-${index}`"
              type="button"
              class="images-slider__pagination-item js-button-images-slider-pagination"
              :class="{
                'images-slider__pagination-item--active':
                  index === getRealSlideIndex(activeIndex),
              }"
              :aria-label="$t('slider.go_to_slide', { number: index + 1 })"
              :aria-current="
                index === getRealSlideIndex(activeIndex) ? 'true' : undefined
              "
              @click="setActiveSlideByOriginal(index)"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';

.images-slider {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__top {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    justify-items: center;
    width: 100%;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: auto auto;
      justify-content: space-between;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__title {
    margin: 0;
    text-align: center;
    @include media-breakpoint-up(md) {
      text-align: left;
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    position: relative;
    gap: 24px;
    @include media-breakpoint-up(sm) {
      gap: 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
    &--with-content {
      @include media-breakpoint-up(md) {
        &:before {
          content: '';
          background-color: $divider;
          right: 0;
          position: absolute;
          top: 184px;
          height: 1px;
          width: 100vw;
          z-index: -1;
        }
      }
      @include media-breakpoint-up(xl) {
        &:before {
          @include responsive-spacing(top, 184);
          @include responsive-spacing(height, 1);
        }
      }
      #{$parent} {
        &__media {
          @include media-breakpoint-up(md) {
            //margin: 0 -48px 0 0;
            //width: calc(66.2% + 48px);
            width: 66.2%;
            position: absolute;
            padding: 0;
            right: 0;
            top: 0;
            z-index: 2;
          }
          @include media-breakpoint-up(xl) {
            //@include responsive-spacing(margin-right, -48);
            //width: calc(66.2% + px-to-vw(48));
          }
        }
        &__pagination {
          margin-top: 40px;
          position: static;
          @include media-breakpoint-up(sm) {
            display: none;
          }
        }
        &__navigation {
          @include media-breakpoint-up(sm) {
            bottom: 0;
            display: flex;
            flex-direction: row;
            gap: 12px;
            position: absolute;
            right: 0;
            z-index: 2;
          }
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(gap, 12);
          }
        }
      }
    }
  }
  &__media {
    display: flex;
    overflow-x: hidden;
    overflow-y: hidden;
    position: relative;
    scroll-behavior: smooth;
    scrollbar-width: none;
    touch-action: pan-y pinch-zoom;
    user-select: none;
    //margin: 0 -16px;
    padding: 0 16px;
    //width: calc(100% + 32px);
    -ms-overflow-style: none;
    width: 100%;
    justify-content: center;
    &::-webkit-scrollbar {
      display: none;
    }
    @include media-breakpoint-up(sm) {
      //margin: 0 -20px;
      //padding: 0 20px;
      //width: calc(100% + 40px);
    }
    @include media-breakpoint-up(md) {
      //margin: 0 -48px;
      //padding: 0 48px;
      //width: calc(100% + 96px);
    }
    @include media-breakpoint-up(xl) {
      //margin: 0 px-to-vw(-48);
      //padding: 0 px-to-vw(48);
      //width: calc(100% + px-to-vw(96));
      &:before {
        @include responsive-spacing(height, 1);
      }
    }
  }
  &__items {
    display: flex;
    gap: 16px;
    margin: 0 auto;
    will-change: transform; // Оптимізація для анімацій
    transition: gap 0.3s ease;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &__item {
    flex-shrink: 0;
    cursor: pointer;
    border-radius: 4px;
    overflow: hidden;
    transition: all 0.3s ease;
    position: relative;
    height: 200px;
    width: 95px;
    &:before {
      background-color: rgba($white, 0.8);
      border-radius: 4px;
      content: '';
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      transition: all 0.3s ease;
      width: 100%;
    }
    @include media-breakpoint-up(sm) {
      border-radius: 8px;
      height: 368;
      width: 184px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(height, 368);
      @include responsive-spacing(width, 184);
      &:before {
        @include responsive-spacing(border-radius, 8);
      }
    }
    &--active {
      width: 200px;
      &:before {
        opacity: 0;
      }
      @include media-breakpoint-up(sm) {
        width: 368px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(width, 368);
      }
    }
    &-image {
      display: block;
      height: 100%;
      object-fit: cover;
      pointer-events: none;
      width: 100%;
    }
  }
  &__navigation {
    display: none;
    @include media-breakpoint-up(sm) {
      display: flex;
      flex-direction: row;
      gap: 12px;
      justify-content: flex-end;
      z-index: 2;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
    }
  }
  &__pagination {
    display: flex;
    justify-content: center;
    gap: 21px;
    @include media-breakpoint-up(sm) {
      display: none;
    }
    &-item {
      border-radius: 50%;
      background-color: $divider;
      border: none;
      cursor: pointer;
      height: 10px;
      padding: 0;
      transition: all 0.3s ease;
      width: 10px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(width, 10);
        @include responsive-spacing(height, 10);
      }
      &--active {
        background-color: $black;
      }
    }
  }
}
.slide-content-enter-active,
.slide-content-leave-active {
  transition: all 0.3s ease;
}

.slide-content-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.slide-content-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
